import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import emailjs from 'emailjs-com';
import { FaLinkedin } from 'react-icons/fa';

const ContactSection = styled.section`
  background-color: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, ${props => props.theme.colors.primary}15 0%, transparent 70%);
    pointer-events: none;
  }
`;

const Container = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
  max-width: 1200px;
  margin: 0 auto;
  padding: 6rem 2rem;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    padding: 4rem 2rem;
  }
`;

const ContactInfo = styled.div`
  .section-title {
    font-size: clamp(2rem, 4vw, 3rem);
    margin-bottom: 1.5rem;

    .highlight {
      color: ${props => props.theme.colors.primary};
    }
  }

  .description {
    color: ${props => props.theme.colors.gray};
    margin-bottom: 2rem;
    font-size: 1.1rem;
    line-height: 1.8;
  }
`;

const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 2rem;

  .contact-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    color: ${props => props.theme.colors.gray};

    .icon {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: ${props => props.theme.colors.dark};
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${props => props.theme.colors.primary};
      font-size: 1.2rem;
    }
  }
`;

const ContactForm = styled(motion.form)`
  background: ${props => props.theme.colors.dark};
  padding: 3rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

  .form-group {
    margin-bottom: 1.5rem;
  }

  label {
    display: block;
    margin-bottom: 0.5rem;
    color: ${props => props.theme.colors.gray};
  }

  input,
  textarea {
    width: 100%;
    padding: 1rem;
    border: 1px solid ${props => props.theme.colors.gray}30;
    border-radius: 8px;
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
    font-size: 1rem;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
    }
  }

  textarea {
    min-height: 150px;
    resize: vertical;
  }

  button {
    width: 100%;
    padding: 1rem;
    border-radius: 8px;
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
    font-size: 1rem;
    font-weight: 600;
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const SuccessMessage = styled(motion.div)`
  text-align: center;
  color: ${props => props.theme.colors.primary};
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
`;

const Contact = () => {
  const [formStatus, setFormStatus] = useState('');
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    try {
      await emailjs.sendForm(
        'service_094yj0j',
        'template_k4qznho',
        e.target,
        'KAOzXXofG1GdaLPWW'
      );
      setFormStatus('success');
      e.target.reset();
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <ContactSection id="contact" ref={ref}>
      <div className="container">
        <Container>
          <ContactInfo>
            <h2 className="section-title">
              Let's <span className="highlight">Connect</span>
            </h2>
            <p className="description">
              Wondering how AI tools and agents could work in your business? Get in touch
              for a conversation about where AI can create real value in your operations —
              and how to integrate it hands-on.
            </p>
            <ContactDetails>
             
              <div className="contact-item">
                <div className="icon">
                  <FaLinkedin />
                </div>
                <a href="https://linkedin.com/in/hayk-avdalyan-3a461949/" target="_blank" rel="noopener noreferrer">
                  LinkedIn
                </a>
              </div>
              <div className="contact-item">
                <div className="icon">📍</div>
                <span>Available for remote consultation worldwide</span>
              </div>
            </ContactDetails>
          </ContactInfo>

          <ContactForm
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input type="text" id="name" name="name" required />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input type="email" id="email" name="email" required />
            </div>
            <div className="form-group">
              <label htmlFor="subject">Subject</label>
              <input type="text" id="subject" name="subject" required />
            </div>
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea id="message" name="message" required></textarea>
            </div>
            <button type="submit" disabled={formStatus === 'sending'}>
              {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
            </button>

            {formStatus === 'success' && (
              <SuccessMessage
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                Message sent successfully!
              </SuccessMessage>
            )}
            
            {formStatus === 'error' && (
              <SuccessMessage
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                style={{ color: 'red' }}
              >
                Error sending message. Please try again.
              </SuccessMessage>
            )}
          </ContactForm>
        </Container>
      </div>
    </ContactSection>
  );
};

export default Contact;
